import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

import { Category } from "./get-category-data";
import { VideoStatus } from "./get-user-behavior";

export type UserBehaviorData =
  | {
      length: string;
      status: VideoStatus;
    }
  | {
      favorite: boolean;
    };

const responseSchema = z.object({
  message: z.string().optional(),
});

type Params = {
  lang: Lang;
  token: string;
  category: Category;
  mediaId: string;
  data: UserBehaviorData;
  videoId?: string;
};

export const updateUserBehavior = async ({
  lang,
  token,
  category,
  mediaId,
  data,
  videoId,
}: Params): Promise<string | undefined> => {
  if (!token) throw new Error("User is not authenticated");

  const endpoint = `/media-behavior/${category}/${mediaId}${
    videoId ? `/${videoId}` : ""
  }`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Accept-Language": lang,
      "X-User-Profile": token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    cache: "no-cache",
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseSchema.parse(await res.json()).message;
};

export const useUpdateUserBehavior = () => {
  const { lang } = useLang();
  const { backendToken } = useAuth();

  return useMutation((params: Omit<Params, "lang" | "token">) =>
    updateUserBehavior({ ...params, lang, token: backendToken ?? "" })
  );
};
